import { Injectable, signal } from '@angular/core';

export type AuthModalMode = 'login' | 'registro';

/**
 * Controla el modal de autenticación del marketplace (drawer derecho en desktop,
 * bottom-sheet en mobile). Reemplaza a la antigua ruta /marketplace/login para no
 * sacar al cliente del servicio que está agendando. Ver SPEC-016.
 *
 * `require()` es la pieza clave del flujo de reserva: abre el modal y devuelve una
 * promesa que resuelve `true` cuando el usuario queda autenticado, de modo que el
 * llamador (p.ej. sede-detail) puede continuar la reserva justo donde la dejó.
 */
@Injectable({ providedIn: 'root' })
export class AuthModalService {
  readonly isOpen = signal(false);
  readonly mode = signal<AuthModalMode>('login');
  private resolver: ((ok: boolean) => void) | null = null;

  /** Abre el modal sin esperar resultado (p.ej. botón "Ingresar" del header). */
  open(mode: AuthModalMode = 'login'): void {
    this.mode.set(mode);
    this.isOpen.set(true);
  }

  /** Abre el modal y resuelve true si el usuario termina autenticado, false si cierra. */
  require(mode: AuthModalMode = 'login'): Promise<boolean> {
    // si ya venía una espera pendiente, la cancelamos (resuelve false)
    this.settle(false);
    this.open(mode);
    return new Promise<boolean>((res) => (this.resolver = res));
  }

  /** Cierra el modal resolviendo la espera pendiente (si la hay). */
  resolve(ok: boolean): void {
    this.isOpen.set(false);
    this.settle(ok);
  }

  /** El usuario cerró el modal sin autenticarse. */
  close(): void {
    this.resolve(false);
  }

  private settle(ok: boolean): void {
    const r = this.resolver;
    this.resolver = null;
    if (r) r(ok);
  }
}
